import React from 'react';
import type { ComponentConfig, Fields } from '@puckeditor/core';
import { sanitizeUrl } from '@braintree/sanitize-url';
import styled from 'styled-components';

import { BORDER_COLOR_AI_SCHEMA, borderStyle, DEFAULT_BORDER } from '../fields/border';
import type { BorderColor, BorderValue } from '../fields/border';
import { CONTAINER_TEXT_COLOR_AI_SCHEMA, containerColorStyle, INHERIT_COLOR } from '../fields/container-colors';
import { PX_AI_SCHEMA } from '../fields/spacing-fields';
import {
  BLOCK_SPACING,
  BRAND_COLOR,
  BRAND_CONTRAST_COLOR,
  BUTTON_FONT_WEIGHT,
  BUTTON_INSET,
  BUTTON_RADIUS,
  HEADING_LINE_HEIGHT,
  HEADING_PRESETS,
  IMAGE_ASPECT_RATIOS,
  IMAGE_FOCAL_POINTS,
  CONTAINER_WIDTHS,
  lookup,
  resolveSurfaceColor,
} from '../fields/tokens';
import type {
  BlockSpacing,
  ContainerWidthOption,
  HeadingPreset,
  ImageAspectRatio,
  ImageFocalPoint,
} from '../fields/tokens';
import { altTextField, resolveAltText } from '../fields/widgets/alt-text-field';
import type { AltTextValue } from '../fields/widgets/alt-text-field';
import { BorderColorField } from '../fields/widgets/border-color-field';
import { ContainerTextColorField } from '../fields/widgets/container-text-color-field';
import { destinationField } from '../fields/widgets/destination-field';
import { focalPointField } from '../fields/widgets/focal-point-field';
import { AssetOrUrlImageSourceField } from '../fields/widgets/image-source-field';
import { NumberField } from '../fields/widgets/number-field';
import { selectField } from '../fields/widgets/select-field';
import { SurfaceColorField, SURFACE_COLOR_FIELD_AI_SCHEMA } from '../fields/widgets/surface-color-field';
import { buildImgixDelivery, isImgixHostedSrc } from '../image-src';
import { interpolateFromPuck } from '../interpolation';
import { resolveImageSource } from '../store-images';
import type { ImageSource } from '../store-images';
import { imgixSizing } from './image-style';
import { isSafeSrc } from './image';

const HEADING_LEVEL_OPTIONS = [
  { label: 'H2', value: 'h2' },
  { label: 'H3', value: 'h3' },
  { label: 'H4', value: 'h4' },
] as const;

const IMAGE_ASPECT_RATIO_OPTIONS: ReadonlyArray<{ label: string; value: ImageAspectRatio }> = [
  { label: 'Original', value: 'original' },
  { label: 'Square (1:1)', value: 'square' },
  { label: 'Landscape (4:3)', value: 'landscape' },
  { label: 'Wide (16:9)', value: 'wide' },
  { label: 'Portrait (3:4)', value: 'portrait' },
];

const IMAGE_FIT_OPTIONS = [
  { label: 'Fill the frame', value: 'cover' },
  { label: 'Fit inside', value: 'contain' },
] as const;

const NEW_TAB_OPTIONS = [
  { label: 'No', value: 'no' },
  { label: 'Yes', value: 'yes' },
] as const;

const ACTION_STYLE_OPTIONS = [
  { label: 'Primary', value: 'primary' },
  { label: 'Secondary', value: 'secondary' },
  { label: 'Tertiary', value: 'tertiary' },
] as const;

export const TILE_BADGE_TONE_OPTIONS = [
  { label: 'Neutral', value: 'neutral' },
  { label: 'Brand', value: 'brand' },
] as const;

const ALIGNMENT_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
] as const;

export const TILE_MAX_WIDTH_OPTIONS: ReadonlyArray<{ label: string; value: ContainerWidthOption }> = [
  { label: 'Narrow', value: 'narrow' },
  { label: 'Normal', value: 'normal' },
  { label: 'Wide', value: 'wide' },
  { label: 'Full', value: 'none' },
];

export const TILE_ALIGN_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
] as const;

const RADIUS_OPTIONS = [
  { label: 'Theme card radius', value: 'theme' },
  { label: 'None', value: 'none' },
] as const;

const PADDING_OPTIONS: ReadonlyArray<{ label: string; value: BlockSpacing }> = [
  { label: 'Extra small (8px)', value: 'xs' },
  { label: 'Small (16px)', value: 'sm' },
  { label: 'Medium (24px)', value: 'md' },
  { label: 'Large (40px)', value: 'lg' },
];

const ACTION_STYLES = {
  primary: {
    background: BRAND_COLOR,
    color: BRAND_CONTRAST_COLOR,
    border: 'none',
  },
  secondary: {
    background: 'transparent',
    color: BRAND_COLOR,
    border: `2px solid ${BRAND_COLOR}`,
  },
  tertiary: {
    background: 'transparent',
    color: BRAND_COLOR,
    border: 'none',
  },
} as const;

const TILE_BADGE_TONES = {
  neutral: {
    background:
      'var(--dcm-cms-tile-badge-neutral-bg, var(--dcm-cms-tile-badge-bg, var(--dcm-color-badge-neutral-bg, var(--dcm-color-surface-muted, #f5f6f7))))',
    color:
      'var(--dcm-cms-tile-badge-neutral-color, var(--dcm-cms-tile-badge-color, var(--dcm-color-badge-neutral-text, #485055)))',
  },
  brand: {
    background: BRAND_COLOR,
    color: BRAND_CONTRAST_COLOR,
  },
} as const;

const CONTENT_ALIGNS = { left: 'left', center: 'center' } as const;
const HEADING_LEVELS = { h2: 'h2', h3: 'h3', h4: 'h4' } as const;
const IMAGE_FITS = { cover: 'cover', contain: 'contain' } as const;
const TILE_RADII = {
  theme: 'var(--dcm-radius-cards, 12px)',
  none: '0',
} as const;
const COMPLETE_HEX = /^#[0-9a-fA-F]{6}$/;
const PIXEL_LENGTH = /^(\d+)px$/;
const MAX_BORDER_WIDTH_PX = 20;

type TileHeadingLevel = keyof typeof HEADING_LEVELS;
type TileImageFit = keyof typeof IMAGE_FITS;
type TileActionStyle = keyof typeof ACTION_STYLES;
type TileBadgeTone = keyof typeof TILE_BADGE_TONES;
type TileContentAlign = keyof typeof CONTENT_ALIGNS;
export type TileAlign = typeof TILE_ALIGN_OPTIONS[number]['value'];
type TileRadius = keyof typeof TILE_RADII;

type TileContent = {
  badge: string;
  heading: string;
  headingLevel: TileHeadingLevel;
  body: string;
};

type TileMedia = {
  source: ImageSource;
  alt: AltTextValue;
  aspectRatio: ImageAspectRatio;
  objectFit: TileImageFit;
  focalPoint: ImageFocalPoint;
};

type TileAction = {
  label: string;
  destination: string;
  openInNewTab: 'no' | 'yes';
  style: TileActionStyle;
};

type TileAppearance = {
  badgeTone: TileBadgeTone;
  surfaceColor: string;
  textColor: string;
  contentAlign: TileContentAlign;
  border: BorderValue;
  radius: TileRadius;
  padding: BlockSpacing;
};

export type TileProps = {
  content: TileContent;
  media: TileMedia;
  action: TileAction;
  appearance: TileAppearance;
  maxWidth: ContainerWidthOption;
  align: TileAlign;
};

const DEFAULT_CONTENT: TileContent = {
  badge: '',
  heading: 'Your tile title',
  headingLevel: 'h3',
  body: 'Add a short description that supports your title.',
};

const DEFAULT_MEDIA: TileMedia = {
  source: { kind: 'url', src: '' },
  alt: { alt: '', decorative: false },
  aspectRatio: 'landscape',
  objectFit: 'cover',
  focalPoint: 'center',
};

const DEFAULT_ACTION: TileAction = {
  label: 'Shop now',
  destination: '/shop',
  openInNewTab: 'no',
  style: 'primary',
};

const DEFAULT_APPEARANCE: TileAppearance = {
  badgeTone: 'neutral',
  surfaceColor: 'surface',
  textColor: INHERIT_COLOR,
  contentAlign: 'left',
  border: DEFAULT_BORDER,
  radius: 'theme',
  padding: 'md',
};

export const DEFAULT_TILE_PROPS: TileProps = {
  content: DEFAULT_CONTENT,
  media: DEFAULT_MEDIA,
  action: DEFAULT_ACTION,
  appearance: DEFAULT_APPEARANCE,
  maxWidth: 'none',
  align: 'center',
};

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as UnknownRecord) : {};
}

function asString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function normalizeBorder(value: unknown): BorderValue {
  const candidate = asRecord(value);
  const candidateColor = asRecord(candidate.color);
  const { color: defaultColor } = DEFAULT_BORDER;
  let color: BorderColor = defaultColor;

  if (candidateColor.style === 'none' || candidateColor.style === 'theme') {
    color = { style: candidateColor.style };
  } else if (
    candidateColor.style === 'custom' &&
    typeof candidateColor.hex === 'string' &&
    COMPLETE_HEX.test(candidateColor.hex)
  ) {
    color = { style: 'custom', hex: candidateColor.hex };
  }

  const candidateWidth = typeof candidate.width === 'string' ? candidate.width : '';
  const widthMatch = PIXEL_LENGTH.exec(candidateWidth);
  const widthPx = widthMatch ? Number(widthMatch[1]) : NaN;

  return {
    color,
    width:
      Number.isInteger(widthPx) && widthPx >= 0 && widthPx <= MAX_BORDER_WIDTH_PX
        ? candidateWidth
        : DEFAULT_BORDER.width,
  };
}

function normalizeSurface(value: unknown): string {
  const surface = asString(value, DEFAULT_APPEARANCE.surfaceColor);
  if (surface === 'none' || surface === 'surface' || surface === 'muted' || COMPLETE_HEX.test(surface)) return surface;
  return DEFAULT_APPEARANCE.surfaceColor;
}

function normalizeTileImageSource(value: unknown): ImageSource {
  const source = asRecord(value);
  if (source.kind === 'url' && typeof source.src === 'string') return { kind: 'url', src: source.src };
  if (source.kind === 'library' && typeof source.assetId === 'string' && typeof source.src === 'string') {
    return { kind: 'library', assetId: source.assetId, src: source.src };
  }
  return DEFAULT_MEDIA.source;
}

export function resolveTileMaxWidth(value: unknown): string {
  if (typeof value === 'string' && Object.prototype.hasOwnProperty.call(CONTAINER_WIDTHS, value)) {
    return CONTAINER_WIDTHS[value as ContainerWidthOption];
  }
  return CONTAINER_WIDTHS.none;
}

export function resolveTileAlign(value: unknown): TileAlign {
  if (value === 'left' || value === 'right') return value;
  return 'center';
}

const TILE_ALIGN_MARGINS: Record<TileAlign, { marginLeft: string; marginRight: string }> = {
  left: { marginLeft: '0', marginRight: 'auto' },
  center: { marginLeft: 'auto', marginRight: 'auto' },
  right: { marginLeft: 'auto', marginRight: '0' },
};

const TileRoot = styled.div`
  background: var(--cms-tile-surface, var(--dcm-color-surface, #ffffff));
  border-radius: var(--cms-tile-radius, var(--dcm-radius-cards, 12px));
  box-sizing: border-box;
  color: var(--cms-tile-text-color, inherit);
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-left: var(--cms-tile-margin-left, auto);
  margin-right: var(--cms-tile-margin-right, auto);
  max-width: var(--cms-tile-max-width, none);
  min-width: 0;
  overflow: hidden;
  text-align: var(--cms-tile-content-align, left);
  width: 100%;
`;

const TileImage = styled.img`
  aspect-ratio: var(--cms-tile-image-aspect-ratio, 4 / 3);
  display: block;
  height: auto;
  object-fit: var(--cms-tile-image-fit, cover);
  object-position: var(--cms-tile-image-focal-point, center center);
  width: 100%;
`;

const TileContent = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--dcm-cms-tile-content-gap, 16px);
  min-width: 0;
  padding: var(--cms-tile-content-padding, 24px);
`;

const TileBadge = styled.span`
  align-self: var(--cms-tile-inline-align, flex-start);
  background: var(--cms-tile-badge-bg, var(--dcm-cms-tile-badge-bg, var(--dcm-color-surface-muted, #f5f6f7)));
  border-radius: var(--dcm-cms-tile-badge-radius, 9999px);
  color: var(--cms-tile-badge-color, var(--dcm-cms-tile-badge-color, #485055));
  font-family: var(--dcm-font-secondary, inherit);
  font-size: var(--dcm-cms-tile-badge-font-size, 12px);
  font-weight: var(--dcm-cms-tile-badge-font-weight, 600);
  line-height: var(--dcm-cms-tile-badge-line-height, 1.3);
  padding: var(--dcm-cms-tile-badge-inset, 4px 8px);
`;

const TileHeading = styled.h3`
  color: inherit;
  font-family: var(--dcm-font-primary, inherit);
  font-size: var(--cms-tile-heading-size, ${HEADING_PRESETS.h3.fontSize});
  font-weight: var(--cms-tile-heading-weight, ${HEADING_PRESETS.h3.fontWeight});
  line-height: ${HEADING_LINE_HEIGHT};
  margin: 0;
`;

const TileBody = styled.p`
  color: inherit;
  font-family: var(--dcm-font-secondary, inherit);
  font-size: var(--dcm-cms-tile-body-font-size, 16px);
  line-height: var(--dcm-line-height-normal, 1.43);
  margin: 0;
  white-space: pre-wrap;
`;

const TileAction = styled.a`
  align-self: var(--cms-tile-inline-align, flex-start);
  background: var(--cms-tile-action-background, ${ACTION_STYLES.primary.background});
  border: var(--cms-tile-action-border, none);
  border-radius: ${BUTTON_RADIUS};
  box-sizing: border-box;
  color: var(--cms-tile-action-color, ${ACTION_STYLES.primary.color});
  cursor: pointer;
  display: inline-block;
  font-family: var(--dcm-font-secondary, inherit);
  font-weight: ${BUTTON_FONT_WEIGHT};
  margin-top: auto;
  padding: ${BUTTON_INSET};
  text-align: center;
  text-decoration: none;

  &:focus-visible {
    outline: var(--dcm-cms-tile-focus-width, 3px) solid
      var(--dcm-cms-tile-focus-color, var(--dcm-color-primary, #2563eb));
    outline-offset: var(--dcm-cms-tile-focus-offset, 3px);
  }
`;

const TileMediaWarning = styled.p`
  background: var(--dcm-cms-tile-warning-bg, #fff8e6);
  color: var(--dcm-cms-tile-warning-color, #7a4300);
  font-family: var(--dcm-font-secondary, inherit);
  font-size: var(--dcm-cms-tile-warning-font-size, 12px);
  line-height: var(--dcm-cms-tile-warning-line-height, 1.4);
  margin: 0;
  padding: var(--dcm-cms-tile-warning-inset, 8px 12px);
`;

/** A constrained, owned-content card. Parent layouts own allocation; Tile may cap and align its own card. */
export const tileConfig: ComponentConfig<TileProps> = {
  label: 'Tile',
  ai: {
    instructions:
      'A constrained content card with an optional badge and image, one plain-text heading and body, and exactly one CTA. Maximum width and alignment make standalone cards usable; Tile still fills the available allocation up to that cap. Tile has no child slot or grid span control; place independent Tiles in Grid Layout for responsive collections.',
  },
  fields: {
    content: {
      type: 'object',
      label: 'Content',
      objectFields: {
        badge: { type: 'text', label: 'Badge', placeholder: 'Optional badge' },
        heading: { type: 'text', label: 'Heading' },
        headingLevel: {
          type: 'select',
          label: 'Heading level',
          options: [...HEADING_LEVEL_OPTIONS],
          ai: { schema: { type: 'string', enum: HEADING_LEVEL_OPTIONS.map(({ value }) => value) } },
        },
        body: { type: 'textarea', label: 'Body' },
      },
    },
    media: {
      type: 'object',
      label: 'Image',
      objectFields: {
        source: {
          type: 'custom',
          label: 'Image source',
          render: AssetOrUrlImageSourceField,
          ai: { exclude: true },
        },
        alt: altTextField(
          'Alt text',
          'Describe an informative image for screen readers, or explicitly mark purely visual media decorative.'
        ),
        aspectRatio: selectField('Shape', IMAGE_ASPECT_RATIO_OPTIONS),
        objectFit: selectField(
          'Crop',
          IMAGE_FIT_OPTIONS,
          "'cover' fills and crops the selected shape; 'contain' keeps the whole image visible."
        ),
        focalPoint: focalPointField(
          'Focal point',
          'Which part of the image remains visible when a fixed shape uses cover cropping.',
          "Only affects fixed image shapes when Crop is 'Fill the frame'."
        ),
      },
    },
    action: {
      type: 'object',
      label: 'Call to action',
      objectFields: {
        label: { type: 'text', label: 'Label' },
        destination: destinationField('Destination', {
          instructions: 'Internal storefront page or external URL for the Tile CTA.',
        }),
        openInNewTab: {
          type: 'radio',
          label: 'Open in new tab',
          options: [...NEW_TAB_OPTIONS],
          ai: { schema: { type: 'string', enum: NEW_TAB_OPTIONS.map(({ value }) => value) } },
        },
        style: {
          type: 'select',
          label: 'Style',
          options: [...ACTION_STYLE_OPTIONS],
          ai: { schema: { type: 'string', enum: ACTION_STYLE_OPTIONS.map(({ value }) => value) } },
        },
      },
    },
    maxWidth: selectField(
      'Maximum width',
      TILE_MAX_WIDTH_OPTIONS,
      "Caps the card width while keeping it responsive. 'Narrow', 'Normal', and 'Wide' use the shared Web Studio container scale; 'Full' fills the available allocation."
    ),
    align: selectField('Alignment', TILE_ALIGN_OPTIONS),
    appearance: {
      type: 'object',
      label: 'Appearance',
      objectFields: {
        badgeTone: {
          type: 'select',
          label: 'Badge tone',
          options: [...TILE_BADGE_TONE_OPTIONS],
          ai: { schema: { type: 'string', enum: TILE_BADGE_TONE_OPTIONS.map(({ value }) => value) } },
        },
        surfaceColor: {
          type: 'custom',
          label: 'Surface',
          render: SurfaceColorField,
          ai: { schema: SURFACE_COLOR_FIELD_AI_SCHEMA },
        },
        textColor: {
          type: 'custom',
          label: 'Text color',
          render: ContainerTextColorField,
          ai: { schema: (CONTAINER_TEXT_COLOR_AI_SCHEMA as unknown) as Record<string, unknown> },
        },
        contentAlign: {
          type: 'radio',
          label: 'Content alignment',
          options: [...ALIGNMENT_OPTIONS],
          ai: { schema: { type: 'string', enum: ALIGNMENT_OPTIONS.map(({ value }) => value) } },
        },
        border: {
          type: 'object',
          label: 'Border',
          objectFields: {
            color: {
              type: 'custom',
              label: 'Color',
              render: BorderColorField,
              ai: { schema: BORDER_COLOR_AI_SCHEMA as Record<string, unknown> },
            },
            width: {
              type: 'custom',
              label: 'Width',
              render: NumberField('px', DEFAULT_BORDER.width),
              ai: { schema: PX_AI_SCHEMA },
            },
          },
        },
        radius: {
          type: 'select',
          label: 'Corner radius',
          options: [...RADIUS_OPTIONS],
          ai: { schema: { type: 'string', enum: RADIUS_OPTIONS.map(({ value }) => value) } },
        },
        padding: {
          type: 'select',
          label: 'Content padding',
          options: PADDING_OPTIONS,
          ai: { schema: { type: 'string', enum: PADDING_OPTIONS.map(({ value }) => value) } },
        },
      },
    },
  },
  resolveFields: (data, { fields }) => {
    const media = asRecord(data.props.media);
    const content = asRecord(data.props.content);
    const effectiveAspectRatio =
      IMAGE_ASPECT_RATIO_OPTIONS.find(({ value }) => value === media.aspectRatio)?.value ?? DEFAULT_MEDIA.aspectRatio;
    const effectiveObjectFit = lookup(
      IMAGE_FITS,
      asString(media.objectFit, DEFAULT_MEDIA.objectFit),
      IMAGE_FITS[DEFAULT_MEDIA.objectFit]
    );
    const hasCroppingFrame = effectiveAspectRatio !== 'original';
    const mediaField = fields.media;
    const appearanceField = fields.appearance;
    if (mediaField.type !== 'object' || appearanceField.type !== 'object') return fields;

    const { source, alt, aspectRatio, objectFit, focalPoint } = mediaField.objectFields;
    const visibleMediaFields = {
      source,
      alt,
      aspectRatio,
      ...(hasCroppingFrame ? { objectFit } : {}),
      ...(hasCroppingFrame && effectiveObjectFit === 'cover' ? { focalPoint } : {}),
    };
    const { badgeTone, ...remainingAppearanceFields } = appearanceField.objectFields;
    const visibleAppearanceFields = {
      ...(typeof content.badge === 'string' && content.badge.trim() !== '' ? { badgeTone } : {}),
      ...remainingAppearanceFields,
    };
    const visibleFields = {
      ...fields,
      media: { ...mediaField, objectFields: visibleMediaFields },
      appearance: { ...appearanceField, objectFields: visibleAppearanceFields },
    } as Fields<TileProps>;

    const isConstrained =
      data.props.maxWidth === 'narrow' || data.props.maxWidth === 'normal' || data.props.maxWidth === 'wide';
    if (isConstrained) return visibleFields;

    const { align: _align, ...fieldsWithoutAlign } = visibleFields;
    // Puck accepts the returned subset as the complete panel field list even though its generic
    // models every declared prop as required.
    return fieldsWithoutAlign as Fields<TileProps>;
  },
  defaultProps: DEFAULT_TILE_PROPS,
  render: (rawProps) => {
    const content = asRecord(rawProps.content);
    const media = asRecord(rawProps.media);
    const action = asRecord(rawProps.action);
    const appearance = asRecord(rawProps.appearance);

    const badge = interpolateFromPuck(asString(content.badge, DEFAULT_CONTENT.badge), rawProps.puck);
    const heading = interpolateFromPuck(asString(content.heading, DEFAULT_CONTENT.heading), rawProps.puck);
    const headingLevel = lookup(
      HEADING_LEVELS,
      asString(content.headingLevel, DEFAULT_CONTENT.headingLevel),
      HEADING_LEVELS[DEFAULT_CONTENT.headingLevel]
    );
    const body = interpolateFromPuck(asString(content.body, DEFAULT_CONTENT.body), rawProps.puck);

    const source = resolveImageSource(normalizeTileImageSource(media.source), undefined);
    const resolvedAlt = resolveAltText(media.alt ?? DEFAULT_MEDIA.alt);
    const alt = resolvedAlt.decorative ? '' : resolvedAlt.alt.trim();
    const aspectRatioKey = asString(media.aspectRatio, DEFAULT_MEDIA.aspectRatio);
    const aspectRatio = lookup(IMAGE_ASPECT_RATIOS, aspectRatioKey, IMAGE_ASPECT_RATIOS[DEFAULT_MEDIA.aspectRatio]);
    const objectFit = lookup(
      IMAGE_FITS,
      asString(media.objectFit, DEFAULT_MEDIA.objectFit),
      IMAGE_FITS[DEFAULT_MEDIA.objectFit]
    );
    const focalPoint = lookup(
      IMAGE_FOCAL_POINTS,
      asString(media.focalPoint, DEFAULT_MEDIA.focalPoint),
      IMAGE_FOCAL_POINTS[DEFAULT_MEDIA.focalPoint]
    );

    const actionLabel = interpolateFromPuck(asString(action.label, DEFAULT_ACTION.label), rawProps.puck).trim();
    const rawDestination = interpolateFromPuck(
      asString(action.destination, DEFAULT_ACTION.destination),
      rawProps.puck
    ).trim();
    const sanitizedDestination = rawDestination === '' ? '' : sanitizeUrl(rawDestination);
    const safeDestination = sanitizedDestination === 'about:blank' ? '' : sanitizedDestination;
    const openInNewTab = asString(action.openInNewTab, DEFAULT_ACTION.openInNewTab) === 'yes';
    const actionStyle = lookup(
      ACTION_STYLES,
      asString(action.style, DEFAULT_ACTION.style),
      ACTION_STYLES[DEFAULT_ACTION.style]
    );

    const badgeTone = lookup(
      TILE_BADGE_TONES,
      asString(appearance.badgeTone, DEFAULT_APPEARANCE.badgeTone),
      TILE_BADGE_TONES[DEFAULT_APPEARANCE.badgeTone]
    );
    const surfaceColor = normalizeSurface(appearance.surfaceColor);
    const textColor = asString(appearance.textColor, DEFAULT_APPEARANCE.textColor);
    const contentAlign = lookup(
      CONTENT_ALIGNS,
      asString(appearance.contentAlign, DEFAULT_APPEARANCE.contentAlign),
      CONTENT_ALIGNS[DEFAULT_APPEARANCE.contentAlign]
    );
    const border = normalizeBorder(appearance.border);
    const radius = lookup(
      TILE_RADII,
      asString(appearance.radius, DEFAULT_APPEARANCE.radius),
      TILE_RADII[DEFAULT_APPEARANCE.radius]
    );
    const padding = lookup(
      BLOCK_SPACING,
      asString(appearance.padding, DEFAULT_APPEARANCE.padding),
      BLOCK_SPACING[DEFAULT_APPEARANCE.padding]
    );
    const maxWidth = resolveTileMaxWidth(rawProps.maxWidth);
    const align = resolveTileAlign(rawProps.align);

    // A selected informative image never reaches production with an empty alt. Puck 0.23 has no
    // publish-validation hook for component fields, so Tile uses the strongest supported boundary:
    // keep the image visible with an explicit editor warning, then fail closed to no media in Render.
    const hasSafeSource = source.src.trim() !== '' && isSafeSrc(source.src);
    const missingInformativeAlt = hasSafeSource && !resolvedAlt.decorative && alt === '';
    // `puck` is present in Puck/Render but direct component tests may omit it.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const isEditing = Boolean(rawProps.puck?.isEditing);
    const shouldRenderImage = hasSafeSource && (!missingInformativeAlt || isEditing);
    const imgixDelivery =
      shouldRenderImage && isImgixHostedSrc(source.src)
        ? buildImgixDelivery(source.src, imgixSizing('full'), 'auto, 100vw')
        : undefined;

    const preset = HEADING_PRESETS[headingLevel as HeadingPreset];
    const rootStyle = {
      ...containerColorStyle(textColor),
      ...borderStyle(border),
      '--cms-tile-surface': resolveSurfaceColor(surfaceColor),
      // Containers scope text through --dcm-color-text-primary instead of setting CSS color.
      // Reading that variable keeps Tile's "Inherit" option honest inside Grid/Section/Flex.
      '--cms-tile-text-color': 'var(--dcm-color-text-primary, #485055)',
      '--cms-tile-content-align': contentAlign,
      '--cms-tile-inline-align': contentAlign === 'center' ? 'center' : 'flex-start',
      '--cms-tile-radius': radius,
      '--cms-tile-content-padding': padding,
      '--cms-tile-max-width': maxWidth,
      '--cms-tile-margin-left': TILE_ALIGN_MARGINS[align].marginLeft,
      '--cms-tile-margin-right': TILE_ALIGN_MARGINS[align].marginRight,
    } as React.CSSProperties;

    const imageStyle = {
      '--cms-tile-image-aspect-ratio': aspectRatio,
      '--cms-tile-image-fit': objectFit,
      '--cms-tile-image-focal-point':
        objectFit === 'cover' && aspectRatioKey !== 'original' ? focalPoint : 'center center',
    } as React.CSSProperties;

    const badgeStyle = {
      '--cms-tile-badge-bg': badgeTone.background,
      '--cms-tile-badge-color': badgeTone.color,
    } as React.CSSProperties;

    const actionStyleProperties = {
      '--cms-tile-action-background': actionStyle.background,
      '--cms-tile-action-color': actionStyle.color,
      '--cms-tile-action-border': actionStyle.border,
    } as React.CSSProperties;

    return (
      <TileRoot data-testid='cms-tile' style={rootStyle}>
        {shouldRenderImage ? (
          <TileImage
            src={imgixDelivery?.src ?? source.src}
            {...(imgixDelivery
              ? {
                  srcSet: imgixDelivery.srcSet,
                  ...(imgixDelivery.sizes !== undefined ? { sizes: imgixDelivery.sizes } : {}),
                }
              : {})}
            alt={alt}
            loading='lazy'
            style={imageStyle}
          />
        ) : null}
        {missingInformativeAlt && isEditing ? (
          <TileMediaWarning data-testid='cms-tile-missing-alt-warning' role='status'>
            This image will not publish until you add alt text or mark it decorative.
          </TileMediaWarning>
        ) : null}
        <TileContent>
          {badge.trim() !== '' ? <TileBadge style={badgeStyle}>{badge}</TileBadge> : null}
          {heading.trim() !== '' ? (
            <TileHeading
              as={headingLevel}
              style={
                {
                  '--cms-tile-heading-size': preset.fontSize,
                  '--cms-tile-heading-weight': preset.fontWeight,
                } as React.CSSProperties
              }
            >
              {heading}
            </TileHeading>
          ) : null}
          {body.trim() !== '' ? <TileBody>{body}</TileBody> : null}
          {actionLabel !== '' && safeDestination !== '' ? (
            <TileAction
              href={safeDestination}
              target={openInNewTab ? '_blank' : undefined}
              rel={openInNewTab ? 'noopener noreferrer' : undefined}
              style={actionStyleProperties}
            >
              {actionLabel}
            </TileAction>
          ) : null}
        </TileContent>
      </TileRoot>
    );
  },
};
